import { useMemo } from "react";
import { alpha, createTheme, PaletteMode, Theme } from "@mui/material";
import { useAtomValue } from "jotai";
import { userLocalPreferences } from "../state/preferences";
import {
	ActivityTypeColors,
	ChartColorsConfiguration,
	ChartColorValues,
	EmissionsTypesColors,
	MethodsColors,
	ThemeColors,
	ThemePadding,
	ThemeShapes,
	TransportTypesColors,
} from "./themeValues";

/**
 * Builds GreenRouter UI theme.
 *
 * @param mode the palette mode
 * @eturns     the theme with the appropriate color mode
 */
export const buildTheme = (mode: PaletteMode): Theme => {
	const colors = buildPalette(mode);

	return createTheme({
		...colors,
		components: {
			...({
				MuiDataGrid: {
					styleOverrides: {
						root: {
							border: "none",
							borderRadius: 8,
							overflow: "hidden",
							background: "none",

							"& .MuiDataGrid-filler": {
								display: "none",
							},

							"& .MuiDataGrid-container--top [role='row'], & .MuiDataGrid-container--bottom [role='row']":
								{
									backgroundColor: "transparent",
									border: "none",
								},

							// Header

							"& .MuiDataGrid-columnHeaders": {
								backgroundColor: colors.palette.background.default,
								minHeight: 56,
								border: "none",
								borderBottom: "none",
							},

							"& .MuiDataGrid-columnHeader": {
								paddingLeft: 16,
								paddingRight: 16,
								borderBottom: "none",
								backgroundColor: "transparent",
							},

							"& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor": {
								border: 0,
							},

							"& .MuiDataGrid-columnHeaderTitle": {
								fontWeight: 600,
								textTransform: "capitalize",
							},

							"& .MuiDataGrid-columnHeaderTitleContainerContent": {
								width: "100%",
							},

							"& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle": {
								color: colors.palette.primary.main,
							},

							// Sord Icon

							// Remove white background from the sort button
							"& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton": {
								backgroundColor: "transparent !important",
								borderRadius: 0,
								padding: 0,
							},

							// Color of the arrow itself
							"& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon": {
								color: ThemeColors.LightContrastText,
							},

							"& .MuiDataGrid-menuIconButton": {
								color: ThemeColors.LightContrastText,
								opacity: 0.5,
								fontSize: 20,
								transition: "all 0.2s ease",
							},

							"& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
								opacity: 0.8,
							},

							"& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-menuIconButton": {
								color: ThemeColors.LightContrastText,
								opacity: 1,
							},

							// Body

							"& .MuiDataGrid-cell": {
								paddingLeft: 16,
								paddingRight: 16,
								backgroundColor: "transparent",
								border: 0,
								borderBottom: `1px solid ${colors.palette.background.default}`,
							},

							"& .MuiDataGrid-row": {
								backgroundColor: "transparent",
							},

							"& .MuiDataGrid-row:hover": {
								backgroundColor: colors.palette.background.default,
							},

							// Checkbox

							"& .MuiDataGrid-columnHeaderTitleContainer .MuiCheckbox-root.MuiDataGrid-checkboxInput": {
								marginLeft: 4,
							},

							"& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
								padding: 0,
							},

							"& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiDataGrid-checkboxInput": {
								color: mode === "dark" ? ThemeColors.DarkContrastText : ThemeColors.LightContrastText,

								"&.Mui-checked": {
									color:
										mode === "dark" ? ThemeColors.DarkContrastText : ThemeColors.LightContrastText,
								},

								"&:hover": {
									backgroundColor: alpha(colors.palette.primary.main, 0.08),
								},
							},

							// Footer

							"& .MuiDataGrid-footerContainer": {
								borderTop: mode === "dark" ? "1px solid #3a3d42" : "1px solid #e0e0e0",
								backgroundColor: "transparent",
							},
							// Focus cleanup

							"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
								outline: "none",
							},

							"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
								outline: "none",
							},
						},
						columnsManagementFooter: {
							display: "none",
						},
					},
				},

				MuiAccordion: {
					defaultProps: {
						disableGutters: true,
						elevation: 0,
					},
					styleOverrides: {
						root: {
							padding: 0,
							border: 0,
							borderBottom: "1px solid #ccc",
							"&::before": {
								display: "none",
							},
						},
					},
				},

				MuiAccordionSummary: {
					styleOverrides: {
						root: {
							padding: 0,
						},
					},
				},

				MuiAccordionDetails: {
					styleOverrides: {
						root: {
							padding: 0,
						},
					},
				},

				MuiPaper: {
					styleOverrides: {
						elevation3: {
							boxShadow:
								"0px 3px 3px -2px rgba(0,0,0,.1),0px 3px 4px 2px rgba(0,0,0,0.07),0px 1px 8px 0px rgba(0,0,0,0.06)",
						},
					},
				},
			} as any),
			MuiCard: {
				styleOverrides: {
					root: {
						height: "100%",
						padding: 0,
						boxShadow: "none",
						borderRadius: ThemeShapes.CardBorderRadius,
						display: "flex",
						flexDirection: "column",
					},
				},
			},
			MuiCardHeader: {
				styleOverrides: {
					root: {
						padding: "0 !important",
						flexBasis: 0,
					},
				},
			},
			MuiCardContent: {
				styleOverrides: {
					root: {
						padding: "0 !important",
					},
				},
			},
			MuiStack: {
				styleOverrides: {
					root: {
						"&.form-switch-container": {
							height: 56,
						},
					},
				},
			},
			MuiToolbar: {
				styleOverrides: {
					root: {
						"&.app-bar": {
							backgroundColor: colors.palette.background.elevation0,
							color: colors.palette.background.contrastText,
						},
					},
				},
			},
			MuiBreadcrumbs: {
				styleOverrides: {
					root: {
						'& [class*="MuiLink-root"]': {
							color: colors.palette.background.contrastText,
							textDecorationColor: colors.palette.background.contrastText,
						},
					},
					li: {
						'& [class*="MuiTypography-root"]': {
							color: colors.palette.background.contrastText,
						},
					},
					separator: {
						color: colors.palette.background.contrastText,
					},
				},
			},

			MuiList: {
				styleOverrides: {
					root: {
						padding: 0,
					},
				},
			},

			MuiListItemIcon: {
				styleOverrides: {
					root: {
						minWidth: 40,
					},
				},
			},
			MuiTableBody: {
				styleOverrides: {
					root: {
						"& .MuiTableRow-root:hover:not(.loading)": {
							backgroundColor: alpha(colors.palette.secondary.main, 0.2),
							cursor: "pointer",
						},
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						"&.centered": {
							textAlign: "center",
						},
					},
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						"&.read-only": {
							backgroundColor: alpha(colors.palette.warning.main, 0.125),
							borderBottom: "none !important",
						},
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: {
						"&.read-only": {
							backgroundColor: alpha(colors.palette.warning.main, 0.125),
							borderBottom: "none !important",
						},
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						"&.warning-box": {
							backgroundColor: alpha(colors.palette.warning.main, 0.5),
							borderRadius: ThemeShapes.CardBorderRadius,
							padding: ThemePadding.Px10,
						},
						"&.user-company": {
							backgroundColor: colors.palette.background.default,
							color: colors.palette.background.contrastText,
							borderRadius: ThemeShapes.ShapeBorderRadius,
							borderStyle: "hidden",
							padding: "8px 14px",
						},
						"&.app-bar-title": {
							color: colors.palette.background.contrastText,
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					notchedOutline: {
						borderColor: colors.palette.primary.main,
					},
					root: {
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: colors.palette.primary,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: colors.palette.primary,
						},
						"&.read-only": {
							backgroundColor: alpha(colors.palette.warning.main, 0.125),
						},
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						minHeight: "40px",
						height: "40px",
						"&.danger-tab": {
							color: colors.palette.error.main,
						},
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					root: {
						minHeight: "40px",
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						"&.fiscal-period-label": {
							// This style is tailor-made exclusively for the app bar fiscal period filter selector.
							backgroundColor: colors.palette.background.default,
							color: colors.palette.background.contrastText,
							top: 0,
							fontSize: "0.75rem",
							position: "absolute",
							paddingLeft: 4,
							paddingRight: 4,
							left: 12,
							transform: "translateY(-50%)",
						},
					},
				},
			},
			MuiButton: {
				defaultProps: {
					size: "medium",
				},
				styleOverrides: {
					sizeMedium: {
						height: "40px",
						minHeight: "40px",
						lineHeight: 1.05,
					},
					root: ({ theme }) => ({
						"&.confirm": {
							backgroundColor: colors.palette.primary.main,
							color: colors.palette.primary.contrastText,
							transition: "opacity 0.125s ease",
							"&.Mui-disabled": {
								opacity: 0.5,
							},
						},

						"&.cancel": {
							backgroundColor: colors.palette.error.main,
							color: colors.palette.primary.contrastText,
							transition: "opacity 0.125s ease",
							"&.Mui-disabled": {
								opacity: 0.5,
							},
						},

						"&.perspective-selected": {
							backgroundColor: colors.palette.secondary.main,
							color: colors.palette.secondary.contrastText,
						},

						"&.perspective-not-selected": {
							backgroundColor: colors.palette.secondary.contrastText,
							color: colors.palette.secondary.main,
						},

						"&.Mui-disabled": {
							"& .MuiTypography-root": {
								color: theme.palette.text.disabled,
							},
							"& .MuiSvgIcon-root": {
								color: theme.palette.action.disabled,
							},
						},
					}),
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: ({ theme }) => ({
						"&.confirm": {
							backgroundColor: colors.palette.primary.main,
							color: colors.palette.primary.contrastText,
							transition: "opacity 0.125s ease",
							"&.Mui-disabled": {
								transition: "opacity 0.125s ease",
								opacity: 0.5,
							},
						},
						"&.top-bar-action": {
							backgroundColor: colors.palette.primary.contrastText,
							color: colors.palette.primary.main,
							transition: "opacity 0.125s ease",
							"&.Mui-disabled": {
								transition: "opacity 0.125s ease",
								opacity: 0.5,
							},
						},
						"&.cancel": {
							backgroundColor: colors.palette.error.main,
							color: colors.palette.primary.contrastText,
							transition: "opacity 0.125s ease",
							"&.Mui-disabled": {
								transition: "opacity 0.125s ease",
								opacity: 0.5,
							},
						},

						"&.Mui-disabled": {
							"& .MuiTypography-root": {
								color: theme.palette.text.disabled,
							},
							"& .MuiSvgIcon-root": {
								color: theme.palette.action.disabled,
							},
						},
					}),
				},
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: {
						"&.rotating-icon": {
							animation: "rotate 2s linear infinite",
						},
					},
				},
			},
			MuiFab: {
				styleOverrides: {
					root: {
						"&.add": {
							right: 20,
							bottom: 20,
							opacity: 0.35,
							position: "fixed",
							transition: "opacity 0.125s ease",
							color: colors.palette.primary.contrastText,
							backgroundColor: colors.palette.primary.main,
							"&.Mui-disabled": {
								transition: "opacity 0.125s ease",
								opacity: 0.5,
							},
						},
						"&:hover": {
							opacity: 1,
							transition: "opacity 0.125s ease",
						},
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					"*": {
						scrollbarWidth: "thin",
						scrollbarColor: `${colors.palette.secondary.main} ${colors.palette.background.default}`,
						"@keyframes rotate": {
							"0%": { transform: "rotate(0deg)" },
							"100%": { transform: "rotate(-360deg)" },
						},
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						"&.italic": {
							fontStyle: "italic",
						},
					},
				},
			},
			MuiSkeleton: {
				styleOverrides: {
					root: {
						"&.loading-field": {
							variant: "rectangular",
							width: "100%",
							height: 84,
						},
					},
				},
			},
			MuiAvatar: {
				styleOverrides: {
					root: {
						"&.top-bar-user-avatar": {
							cursor: "pointer",
							color: colors.palette.background.contrastText,
							border: `2px solid ${colors.palette.background.contrastText}80`,
							background: `linear-gradient(45deg, ${colors.palette.warning.main} 50%, ${colors.palette.secondary.main} 100%)`,
						},
					},
				},
			},
			MuiLinearProgress: {
				styleOverrides: {
					root: {
						"&.chart-bar": {
							height: 16,
							borderRadius: 4,
						},
					},
				},
			},
		},
		shape: {
			borderRadius: ThemeShapes.ShapeBorderRadius,
		},
		typography: {
			fontFamily: `'DM Sans', 'Roboto', 'Helvetica', 'Arial', sans-serif, monospace`,
			allVariants: {
				fontVariantNumeric: "tabular-nums",
			},
			htmlFontSize: 16, //  1rem = 16px

			fontSize: 14, // base font size in px (used for body1)
			h1: {
				fontSize: "5.3125rem", // 85px
				fontWeight: 700,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
			},
			h2: {
				fontSize: "3.3125rem", // 53px
				fontWeight: 700,
				lineHeight: 1.2,
				letterSpacing: "-0.00833em",
			},
			h3: {
				fontSize: "2.625rem", // 42px
				fontWeight: 700,
				lineHeight: 1.2,
				letterSpacing: "0em",
			},
			h4: {
				fontSize: "2rem", // 32px
				fontWeight: 700,
				lineHeight: 1.25,
				letterSpacing: "0.00735em",
			},
			h5: {
				fontSize: "1.375rem", // 22px
				fontWeight: 700,
				lineHeight: 1.334,
				letterSpacing: "0em",
			},
			h6: {
				fontSize: "1.125rem", // 18px
				fontWeight: 700,
				lineHeight: 1.6,
				letterSpacing: "0.0075em",
			},
			subtitle1: {
				fontSize: "1rem", // 16px
				fontWeight: 600,
				lineHeight: 1.75,
				letterSpacing: "0.00938em",
			},
			subtitle2: {
				fontSize: "0.875rem", // 14px
				fontWeight: 600,
				lineHeight: 1.57,
				letterSpacing: "0.00714em",
			},
			body1: {
				fontSize: "1rem", // 16px
				fontWeight: 400,
				lineHeight: 1.5,
				letterSpacing: "0.00938em",
			},
			body2: {
				fontSize: "0.875rem", // 14px
				fontWeight: 400,
				lineHeight: 1.43,
				letterSpacing: "0.01071em",
			},
			button: {
				fontSize: "0.875rem", // 14px
				fontWeight: 600,
				textTransform: "none",
				lineHeight: 1.75,
				letterSpacing: "0.02857em",
			},
			caption: {
				fontSize: "0.6875rem", // 11px
				fontWeight: 400,
				lineHeight: 1.66,
				letterSpacing: "0.03333em",
			},
			overline: {
				fontSize: "0.625rem", // 10px
				fontWeight: 600,
				letterSpacing: "0.05em",
				lineHeight: 2.66,
				textTransform: "uppercase",
			},
		},
	});
};

/**
 * Listens for theme mode changes.
 *
 * @returns the current theme
 */
export const useCurrentTheme = () => {
	const preferences = useAtomValue(userLocalPreferences);

	const theme = useMemo(
		() => buildTheme(preferences.theme === "system" ? (preferences.systemTheme ?? "light") : preferences.theme),
		[preferences.theme, preferences.systemTheme]
	);

	return { theme };
};

/**
 * Exports chart colors configurations according to the current theme.
 */
export const useChartColors = () => {
	const preferences = useAtomValue(userLocalPreferences);

	const chartColors: ChartColorsConfiguration = useMemo(() => {
		const mode = preferences.theme === "system" ? (preferences.systemTheme ?? "light") : preferences.theme;

		switch (mode) {
			case "light":
				return {
					scope1Bar: ChartColorValues.LightScope1Bar,
					scope2Bar: ChartColorValues.LightScope2Bar,
					scope3Bar: ChartColorValues.LightScope3Bar,
					roadType: TransportTypesColors.LightRoad,
					trainType: TransportTypesColors.LightTrain,
					shipType: TransportTypesColors.LightShip,
					airType: TransportTypesColors.LightAir,
					electricityType: EmissionsTypesColors.LightElectricity,
					fuelType: EmissionsTypesColors.LightFuel,
					refrigerantType: EmissionsTypesColors.LightRefrigerant,
					heatType: EmissionsTypesColors.LightHeat,
					processGasesType: EmissionsTypesColors.LightProcessGases,
					waterType: EmissionsTypesColors.LightWater,
					transportType: ActivityTypeColors.LightTransport,
					operativeUnitsType: ActivityTypeColors.LightOperativeUnits,
					otherType: ActivityTypeColors.LightOther,
					method1: MethodsColors.Method1,
					method2: MethodsColors.Method2,
					method3: MethodsColors.Method3,
					method4: MethodsColors.Method4,
					method5: MethodsColors.Method5,
					method6: MethodsColors.Method6,
				};
			case "dark":
				return {
					scope1Bar: ChartColorValues.DarkScope1Bar,
					scope2Bar: ChartColorValues.DarkScope2Bar,
					scope3Bar: ChartColorValues.DarkScope3Bar,
					roadType: TransportTypesColors.DarkRoad,
					trainType: TransportTypesColors.DarkTrain,
					shipType: TransportTypesColors.DarkShip,
					airType: TransportTypesColors.DarkAir,
					electricityType: EmissionsTypesColors.DarkElectricity,
					fuelType: EmissionsTypesColors.DarkFuel,
					refrigerantType: EmissionsTypesColors.DarkRefrigerant,
					heatType: EmissionsTypesColors.DarkHeat,
					processGasesType: EmissionsTypesColors.DarkProcessGases,
					waterType: EmissionsTypesColors.DarkWater,
					transportType: ActivityTypeColors.DarkTransport,
					operativeUnitsType: ActivityTypeColors.DarkOperativeUnits,
					otherType: ActivityTypeColors.DarkOther,
					method1: MethodsColors.DarkMethod1,
					method2: MethodsColors.DarkMethod2,
					method3: MethodsColors.DarkMethod3,
					method4: MethodsColors.DarkMethod4,
					method5: MethodsColors.DarkMethod5,
					method6: MethodsColors.DarkMethod6,
				};
		}
	}, [preferences.theme, preferences.systemTheme]);

	return { chartColors };
};

/**
 * Computes GreenRouter UI color scheme (light, dark).
 *
 * @param mode the palette mode
 * @returns    the theme color palette according to its mode
 */
const buildPalette = (mode: PaletteMode) => ({
	palette: {
		mode,
		...(mode === "dark" ? darkPalette() : lightPalette()),
	},
});

// Used internally.
const darkPalette = () => ({
	primary: {
		main: ThemeColors.DarkPrimary,
		contrastText: ThemeColors.DarkContrastText,
	},
	secondary: {
		main: ThemeColors.DarkSecondary,
		contrastText: ThemeColors.DarkContrastText,
	},
	error: {
		main: ThemeColors.DarkError,
	},
	success: {
		main: ThemeColors.DarkSuccess,
	},
	warning: {
		main: ThemeColors.DarkWarning,
	},
	text: {
		primary: ThemeColors.LightContrastText,
	},
	background: {
		default: ThemeColors.DarkBackground,
		contrastText: ThemeColors.DarkBackgroundContrastText,
		elevation0: ThemeColors.DarkBackgroundElevation0,
		elevation1: ThemeColors.DarkBackgroundElevation1,
	},
});

// Used internally.
const lightPalette = () => ({
	primary: {
		main: ThemeColors.LightPrimary,
		dark: ThemeColors.LightPrimaryDark,
		light: ThemeColors.LightPrimaryLight,
		contrastText: ThemeColors.LightContrastText,
	},
	secondary: {
		main: ThemeColors.LightSecondary,
		dark: ThemeColors.LightSecondaryDark,
		light: ThemeColors.LightSecondaryLight,
		contrastText: ThemeColors.LightContrastText,
	},
	error: {
		main: ThemeColors.LightError,
	},
	success: {
		main: ThemeColors.LightSuccess,
	},
	warning: {
		main: ThemeColors.LightWarning,
	},
	info: {
		main: ThemeColors.LightInfo,
	},
	text: {
		primary: ThemeColors.DarkContrastText,
	},
	background: {
		default: ThemeColors.LightBackground,
		contrastText: ThemeColors.LightBackgroundContrastText,
		elevation0: ThemeColors.LightBackgroundElevation0,
		elevation1: ThemeColors.LightBackgroundElevation1,
	},
});
